<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                {{ editingMaintenance ? "Edit Asset Maintenance" : "Add New Asset Maintenance" }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit" ref="form">
                  <v-row class="pa-1 py-2 px-2">
                    <!-- Maintenance Date -->
                    <v-col md="3" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="maintenance_date.value.value"
                        label="Maintenance Date *"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        :error-messages="maintenance_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <!-- Location -->
                    <v-col md="3" cols="12">
                      <v-autocomplete
                        v-model="asset_location_id.value.value"
                        label="Maintenance Location *"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :items="getAssetLocationsList"
                        :item-title="(item) => `${item.name}`"
                        item-value="id"
                        :error-messages="asset_location_id.errorMessage.value"
                        :loading="locationLoading"
                        prepend-inner-icon="mdi-map-marker"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                    </v-col>

                    <!-- Supplier -->
                    <v-col md="3" cols="12">
                      <v-autocomplete
                        v-model="supplier_id.value.value"
                        label="Supplier/Vendor"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :items="getSupplierList"
                        :item-title="(item) => `${item.supplier_name}`"
                        item-value="id"
                        :error-messages="supplier_id.errorMessage.value"
                        :loading="supplierLoading"
                        prepend-inner-icon="mdi-account-group"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                    </v-col>

                    <!-- Reference No -->
                    <v-col md="3" cols="12">
                      <v-text-field
                        v-model="reference_no.value.value"
                        label="Reference No"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :error-messages="reference_no.errorMessage.value"
                        prepend-inner-icon="mdi-file-document-outline"
                      ></v-text-field>
                    </v-col>

                  </v-row>

                  <!-- Assets Selection Section -->
                  <v-row class="py-2 px-2">
                    <v-col cols="12">
                      <v-card variant="outlined" class="pa-4">
                        <v-card-title class="pa-2">
                          <span class="text-h6">Select Assets for Maintenance</span>
                        </v-card-title>
                        <v-card-text>
                          <!-- Asset Search -->
                          <v-row class="pb-2">
                            <v-col md="6" cols="12">
                              <v-autocomplete
                                v-model="selectedAssetId"
                                label="Search and Add Asset"
                                variant="outlined"
                                density="compact"
                                bg-color="grey-lighten-4"
                                :items="availableAssets"
                                :item-title="(item) => `${item.code} - ${item.name}`"
                                item-value="id"
                                :loading="assetLoading"
                                return-object
                                prepend-inner-icon="mdi-magnify"
                                clearable
                              
                                  :menu-props="{ contentClass: 'my-custom-dropdown' }"
>
                                <template v-slot:item="{ props, item }">
                                  <v-list-item v-bind="props">
                                    <template v-slot:title>
                                      <div>
                                        <strong>{{ item.raw.code }}</strong> - {{ item.raw.name }}
                                      </div>
                                      <div class="text-caption text-grey">
                                        Category: {{ item.raw.asset_category?.name }} | 
                                        Location: {{ item.raw.asset_location?.name }} | 
                                        Status: {{ item.raw.asset_status?.name }}
                                      </div>
                                    </template>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                            <v-col md="2" cols="12">
                              <v-btn
                                color="primary"
                                block
                                @click="addAssetToMaintenance"
                                :disabled="!selectedAssetId"
                                prepend-icon="mdi-plus"
                              >
                                Add Asset
                              </v-btn>
                            </v-col>
                          </v-row>

                          <!-- Selected Assets Table -->
                          <v-data-table
                            :headers="assetHeaders"
                            :items="maintenanceAssets"
                            :items-per-page="5"
                            density="compact"
                            class="elevation-1"
                          >
                            <template v-slot:item.code="{ item }">
                              <strong>{{ item.code }}</strong>
                            </template>
                            <template v-slot:item.status="{ item }">
                              <v-chip size="small" color="blue" variant="tonal">
                                {{ item.asset_status?.name }}
                              </v-chip>
                            </template>
                            <template v-slot:item.issue_description="{ item }">
                              <v-text-field class="py-2 "
                                v-model="item.issue_description"
                                density="compact"
                                variant="outlined"
                                hide-details
                                placeholder="Enter issue description"
                              ></v-text-field>
                            </template>
                            <template v-slot:item.action_taken="{ item }">
                              <v-text-field
                                v-model="item.action_taken"
                                density="compact"
                                variant="outlined"
                                hide-details
                                placeholder="Enter action taken"
                              ></v-text-field>
                            </template>
                            <template v-slot:item.cost="{ item }">
                              <v-text-field
                                v-model="item.cost"
                                type="number"
                                density="compact"
                                variant="outlined"
                                hide-details
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              ></v-text-field>
                            </template>
                            <template v-slot:item.actions="{ item }">
                              <v-btn
                                icon="mdi-delete"
                                size="small"
                                variant="text"
                                color="error"
                                @click="removeAssetFromMaintenance(item)"
                              />
                            </template>
                            <template v-slot:no-data>
                              <div class="text-center py-4">
                                <v-icon size="48" color="grey">mdi-wrench</v-icon>
                                <p class="text-grey">No assets added yet</p>
                              </div>
                            </template>
                            <template v-slot:bottom>
                              <div class="pa-4">
                                <v-row>
                                  <v-col cols="12" class="text-right">
                                    <strong>Total Maintenance Cost:</strong> 
                                    <span class="text-h6 text-primary ml-2">{{ totalCost.toFixed(2) }}</span>
                                  </v-col>
                                </v-row>
                              </div>
                            </template>
                          </v-data-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Remarks -->
                  <v-col cols="12">
                    <v-textarea
                      v-model="remarks.value.value"
                      label="Remarks"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      rows="2"
                      :error-messages="remarks.errorMessage.value"
                    ></v-textarea>
                  </v-col>

                  <!-- Action Buttons -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <div class="d-flex justify-end" style="gap: 12px;">
                        <v-btn
                          class="bg-grey-darken-1 me-2"
                          variant="flat"
                          type="button"
                          :loading="saving && currentAction === 0"
                          :disabled="saving"
                          @click="submitForm(0)"
                        >
                          DRAFT
                        </v-btn>
                        <v-btn
                          color="primary"
                          type="button"
                          :loading="saving && currentAction === 1"
                          :disabled="saving && currentAction !== 1"
                          prepend-icon="mdi-content-save"
                          @click="submitForm(1)"
                        >
                          {{ editingMaintenance ? "Update & Post" : "POST" }}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import { getAssetRegistrationsList, getSupplierList } from "@/store/api";
import Axios from "axios";

export default {
  components: {},
  data() {
    return {
      panel: [0],
      locationLoading: false,
      supplierLoading: false,
      assetLoading: false,
      saving: false,
      currentAction: null,
      selectedAssetId: null,
      maintenanceAssets: [],
      allAssets: [],
      supplierList: [],
      assetHeaders: [
        { title: "Code", key: "code", align: "left", sortable: false },
        { title: "Name", key: "name", align: "left", sortable: false },
        { title: "Category", key: "asset_category.name", align: "left", sortable: false },
        { title: "Status", key: "status", align: "left", sortable: false },
        { title: "Issue Description", key: "issue_description", align: "left", sortable: false, width: "200px" },
        { title: "Action Taken", key: "action_taken", align: "left", sortable: false, width: "200px" },
        { title: "Cost", key: "cost", align: "left", sortable: false, width: "120px" },
        { title: "Actions", key: "actions", align: "center", sortable: false, width: "80px" },
      ],
      currentStatus: 1,
    };
  },
  computed: {
    editingMaintenance() {
      return this.$store.getters.getEditingAssetMaintenance;
    },
    getAssetLocationsList() {
      let data = this.$store.getters.getAssetLocationsList;
      if (data.length > 0) {
        return data.filter((item) => item.status == 1);
      }
      return [];
    },
    getSupplierList() {
      return this.supplierList;
    },
    availableAssets() {
      // Filter assets that are not already selected
      return this.allAssets.filter((asset) => {
        return !this.maintenanceAssets.some((selected) => selected.id === asset.id);
      });
    },
    totalCost() {
      return this.maintenanceAssets.reduce((sum, asset) => {
        return sum + (parseFloat(asset.cost) || 0);
      }, 0);
    },
  },
  watch: {
    editingMaintenance: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadEditDataById(newVal.id);
        }
      },
      immediate: true,
    },
  },
  created() {
    this.loadLocations();
    this.loadSuppliers();
    this.loadAssets();
  },
  setup() {
    const store = useStore();

    const validationSchema = yup.object({
      maintenance_date: yup.string().required("Maintenance date is required"),
      asset_location_id: yup.number().required("Location is required"),
      supplier_id: yup.number().nullable(),
      reference_no: yup.string().nullable(),
      remarks: yup.string().nullable(),
    });

    const { handleSubmit, resetForm } = useForm({
      validationSchema,
    });

    const maintenance_date = useField("maintenance_date");
    const asset_location_id = useField("asset_location_id");
    const supplier_id = useField("supplier_id");
    const reference_no = useField("reference_no");
    const remarks = useField("remarks");

    return {
      maintenance_date,
      asset_location_id,
      supplier_id,
      reference_no,
      remarks,
      handleSubmit,
      resetForm,
    };
  },
  methods: {
    async submitForm(status) {
      this.currentStatus = typeof status !== "undefined" ? status : 1;
      this.currentAction = this.currentStatus;
      try {
        await this.submit();
      } finally {
        this.currentAction = null;
      }
    },
    async submit() {
      try {
        // Check if at least one asset is selected
        if (this.maintenanceAssets.length === 0) {
          toast.error("Please add at least one asset for maintenance");
          return;
        }

        // Validate that all assets have issue descriptions
        const hasEmptyDescription = this.maintenanceAssets.some(
          (asset) => !asset.issue_description || asset.issue_description.trim() === ""
        );
        if (hasEmptyDescription) {
          toast.error("Please provide issue description for all assets");
          return;
        }

        // Validate and submit
        await this.handleSubmit(async (values) => {
          this.saving = true;
          const maintenanceData = {
            maintenance_date: values.maintenance_date,
            asset_location_id: values.asset_location_id,
            supplier_id: values.supplier_id || null,
            reference_no: values.reference_no || null,
            remarks: values.remarks || null,
            status: this.currentStatus ?? 1,
            asset_maintenance_master_id: this.editingMaintenance?.id || null,
            assets: this.maintenanceAssets.map((asset) => ({
              asset_register_id: asset.id,
              issue_description: asset.issue_description || "",
              action_taken: asset.action_taken || "",
              cost: (asset.cost || 0).toString(),
            })),
          };

          try {
            const response = await this.$store.dispatch("saveUpdateAssetMaintenance", maintenanceData);
            if (response.data.success) {
              toast.success(response.data.message || "Asset maintenance saved successfully");
              this.$store.commit("UNSET_EDITING_ASSET_MAINTENANCE");
              this.$router.push({ name: "maintenance-records" });
            } else {
              toast.error(response.data.message || "Failed to save asset maintenance");
            }
          } catch (error) {
            console.error("Error saving maintenance:", error);
            console.error("Error response:", error.response?.data);
            
            // Handle validation errors
            if (error.response?.data?.errors) {
              const errors = error.response.data.errors;
              Object.keys(errors).forEach(key => {
                toast.error(errors[key][0]);
              });
            } else {
              toast.error(error.response?.data?.message || "Failed to save asset maintenance");
            }
          } finally {
            this.saving = false;
          }
        })();
      } catch (error) {
        console.error("Validation error:", error);
      }
    },
    loadLocations() {
      // if (this.$store.getters.getAssetLocationsList.length == 0) {
        this.locationLoading = true;
        this.$store.dispatch("getAssetLocationsList").then(() => {
          this.locationLoading = false;
        });
      // }
    },
    async loadSuppliers() {
      this.supplierLoading = true;
      try {
        const response = await Axios.post(getSupplierList, {});
        this.supplierList = response.data.data || [];
      } catch (error) {
        console.error("Error loading suppliers:", error);
        this.supplierList = [];
      } finally {
        this.supplierLoading = false;
      }
    },
    async loadAssets() {
      this.assetLoading = true;
      try {
        const response = await Axios.get(getAssetRegistrationsList, {
          params: {},
        });
        this.allAssets = response.data.data || [];
      } catch (error) {
        console.error("Error loading assets:", error);
        this.allAssets = [];
      } finally {
        this.assetLoading = false;
      }
    },
    async loadEditDataById(id) {
      try {
        // Ensure assets are loaded first
        if (this.allAssets.length === 0) {
          await this.loadAssets();
        }
        
        const response = await this.$store.dispatch("getAssetMaintenanceById", id);
        console.log("Maintenance data response:", response);
        if (response && response.data) {
          this.loadEditData(response.data);
        }
      } catch (error) {
        console.error("Error loading maintenance data:", error);
        toast.error("Failed to load maintenance data");
      }
    },
    loadEditData(maintenance) {
      if (maintenance) {
        console.log("Loading edit data:", maintenance);
        this.maintenance_date.value.value = maintenance.maintenance_date;
        this.asset_location_id.value.value = maintenance.asset_location_id;
        this.supplier_id.value.value = maintenance.supplier_id;
        this.reference_no.value.value = maintenance.reference_no || "";
        this.remarks.value.value = maintenance.remarks || "";
        this.currentStatus = typeof maintenance.status !== "undefined" ? maintenance.status : 1;

        // Load existing maintenance assets with full details
        if (maintenance.details && maintenance.details.length > 0) {
          console.log("Maintenance details:", maintenance.details);
          this.maintenanceAssets = maintenance.details.map((detail) => {
            let asset = null;
            // If assetRegister is loaded properly
            if (detail.assetRegister && detail.assetRegister.id) {
              asset = detail.assetRegister;
            } else if (detail.asset_register_id) {
              // Try to find the asset from the loaded assets list
              asset = this.allAssets.find(a => a.id === detail.asset_register_id);
            }

            if (asset) {
              // Add maintenance specific fields to the asset
              return {
                ...asset,
                issue_description: detail.issue_description || "",
                action_taken: detail.action_taken || "",
                cost: detail.cost || 0,
              };
            }
            return null;
          }).filter(Boolean);
          console.log("Loaded maintenance assets:", this.maintenanceAssets);
        }
      }
    },
    addAssetToMaintenance() {
      if (!this.selectedAssetId) {
        toast.error("Please select an asset");
        return;
      }

      // Check if asset is already added
      if (this.maintenanceAssets.some((asset) => asset.id === this.selectedAssetId.id)) {
        toast.warning("This asset is already added");
        return;
      }

      // Add asset with default maintenance fields
      this.maintenanceAssets.push({
        ...this.selectedAssetId,
        issue_description: "",
        action_taken: "",
        cost: 0,
      });
      this.selectedAssetId = null;
      toast.success("Asset added successfully");
    },
    removeAssetFromMaintenance(asset) {
      const index = this.maintenanceAssets.findIndex((a) => a.id === asset.id);
      if (index > -1) {
        this.maintenanceAssets.splice(index, 1);
        toast.success("Asset removed");
      }
    },
    goBack() {
      this.$store.commit("UNSET_EDITING_ASSET_MAINTENANCE");
      this.$router.push({ name: "maintenance-records" });
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

